/**
 * 团队工作台面板（扩展③+⑥）：备赛进度条 + 共享文件中心 + 决策日志 + 导师反馈。
 * 挂在队伍页的「工作台」Tab：成队前只有文件/决策，成队后追加进度条。
 */
import { useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { advisorFeedbackApi } from '../api/advisor-feedback.js';
import { API_BASE } from '../api/client.js';
import { toUserMessage } from '../api/errors.js';
import { fileToBase64, formatSize, workbenchApi } from '../api/workbench.js';
import type { AdvisorFeedbackLinkDto, TeamDto } from '../api/types.js';
import { Button } from './Button.js';
import { ErrorState } from './ErrorState.js';
import { Loading } from './Loading.js';
import { Tag } from './Tag.js';
import {
  useProjectProgressQuery,
  useTeamDecisionsQuery,
  useTeamFeedbackQuery,
  useTeamFilesQuery,
} from '../hooks/queries.js';
import { PrepTimeline } from './PrepTimeline.js';
import { TeamHealthPanel } from './TeamHealthPanel.js';
import { IncubatorPanel } from './IncubatorPanel.js';
import { formatDateTime } from '../lib/format.js';
import { qk } from '../lib/queryKeys.js';
import { getAccessToken } from '../store/session.js';
import { toast } from '../store/toast.js';

const FILE_ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.md,.png,.jpg,.jpeg,.gif,.zip';

/** 带 Bearer 的认证下载：fetch → Blob → 临时 objectURL 触发浏览器另存。 */
async function downloadWithAuth(url: string, fallbackName: string): Promise<void> {
  const token = getAccessToken();
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`下载失败（HTTP ${response.status}）`);
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const name = match?.[1] ? decodeURIComponent(match[1]) : fallbackName;
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export function WorkbenchPanel({ team, meId, projectId }: { team: TeamDto; meId: string; projectId: string | null }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [decisionTitle, setDecisionTitle] = useState('');
  const [decisionDetail, setDecisionDetail] = useState('');
  const [decisionError, setDecisionError] = useState<string | null>(null);
  /** 新生成的反馈链接（明文只展示一次，关掉就没了） */
  const [freshLink, setFreshLink] = useState<AdvisorFeedbackLinkDto | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const filesQuery = useTeamFilesQuery(team.id);
  const decisionsQuery = useTeamDecisionsQuery(team.id);
  const progressQuery = useProjectProgressQuery(projectId);
  // 行动建议是提交后异步生成的：有待生成项时每 2 秒轮询一次，全部就绪即停
  const feedbackQuery = useTeamFeedbackQuery(team.id, (query) =>
    (query.state.data?.feedback ?? []).some((item) => item.suggestion === null) ? 2000 : false,
  );

  const isOwner = team.ownerId === meId;

  const upload = useMutation({
    mutationFn: (input: { name: string; contentBase64: string }) => workbenchApi.upload(team.id, input),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: qk.teamFiles(team.id) });
      setUploadError(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success(`「${data?.file?.name ?? '文件'}」已上传`, '队伍成员都可以在工作台下载');
    },
    onError: (error) => {
      setUploadError(`上传失败：${toUserMessage(error)}`);
    },
  });

  const removeFile = useMutation({
    mutationFn: (fileId: string) => workbenchApi.remove(team.id, fileId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.teamFiles(team.id) });
      toast.success('文件已删除', '');
    },
    onError: (error) => {
      toast.error('删除失败', toUserMessage(error));
    },
  });

  const addDecision = useMutation({
    mutationFn: (input: { title: string; detail?: string }) => workbenchApi.addDecision(team.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.teamDecisions(team.id) });
      setDecisionTitle('');
      setDecisionDetail('');
      setDecisionError(null);
      toast.success('决策已记录', '只增不改，可随时回查');
    },
    onError: (error) => {
      setDecisionError(`记录决策失败：${toUserMessage(error)}`);
    },
  });

  /** 生成导师反馈链接（扩展⑥）：明文只展示一次 */
  const createFeedbackLink = useMutation({
    mutationFn: () => advisorFeedbackApi.createLink(team.id),
    onSuccess: (link) => {
      setFreshLink(link);
      setLinkError(null);
      toast.success('反馈链接已生成', '7 天内有效、只接受一次反馈；把链接发给导师即可');
    },
    onError: (error) => {
      setFreshLink(null);
      setLinkError(toUserMessage(error));
    },
  });

  const handlePickFile = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file === undefined) return;
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('上传失败：文件超过 10MB 上限（申报书类文档一般远小于此）');
      event.target.value = '';
      return;
    }
    setUploadError(null);
    fileToBase64(file)
      .then((contentBase64) => upload.mutate({ name: file.name, contentBase64 }))
      .catch(() => setUploadError('上传失败：读取本地文件出错，请重试'));
  };

  const handleDownload = (fileId: string, name: string): void => {
    downloadWithAuth(`${API_BASE}${workbenchApi.downloadPath(team.id, fileId)}`, name).catch((error: unknown) => {
      toast.error('下载失败', error instanceof Error ? error.message : '请稍后重试');
    });
  };

  const handleAddDecision = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const title = decisionTitle.trim();
    if (title.length === 0) {
      setDecisionError('记录决策失败：请填写决策标题（例如「确定选题」「周三晚例会」）');
      return;
    }
    setDecisionError(null);
    addDecision.mutate({ title, detail: decisionDetail.trim() });
  };

  const progress = progressQuery.data ?? null;

  return (
    <div className="stack">
      <section className="stack-sm" aria-label="项目孵化器">
        <div className="row-tight">
          <span className="card-sub">项目孵化器（先定方向，再谈执行）</span>
        </div>
        <IncubatorPanel team={team} meId={meId} />
      </section>

      <hr className="divider" />

      <section className="stack-sm" aria-label="AI 项目体检">
        <div className="row-tight">
          <span className="card-sub">AI 项目体检</span>
        </div>
        <TeamHealthPanel teamId={team.id} />
      </section>

      <hr className="divider" />

      {projectId !== null ? (
        <section className="stack-sm" aria-label="备赛进度">
          <div className="row-tight">
            <span className="card-sub">备赛进度（权重 15 / 35 / 35 / 15）</span>
            {progressQuery.isSuccess && progress !== null ? (
              <Tag tone={progress.overall >= 100 ? 'ok' : 'primary'}>{progress.overall}%</Tag>
            ) : null}
          </div>
          {progressQuery.isPending ? (
            <Loading label="正在读取进度…" rows={1} />
          ) : progressQuery.isError ? (
            <ErrorState error={progressQuery.error} title="读取进度失败" onRetry={() => void progressQuery.refetch()} />
          ) : progress !== null ? (
            <PrepTimeline progress={progress} eventCode={team.eventCode} />
          ) : null}
        </section>
      ) : (
        <p className="muted small">确认成队后显示备赛进度。</p>
      )}

      <hr className="divider" />

      <section className="stack-sm" aria-label="共享文件中心">
        <div className="row-tight">
          <span className="card-sub">共享文件中心（申报书 / PPT / 数据文件，全队可见）</span>
          <span className="right">
            <input
              ref={fileInputRef}
              type="file"
              accept={FILE_ACCEPT}
              className="visually-hidden"
              aria-label="选择要上传的文件"
              onChange={handlePickFile}
            />
            <Button variant="secondary" size="sm" loading={upload.isPending} onClick={() => fileInputRef.current?.click()}>
              {upload.isPending ? '正在上传…' : '上传文件'}
            </Button>
          </span>
        </div>
        {uploadError !== null ? (
          <div className="banner banner-danger" role="alert">
            <span className="banner-ico" aria-hidden="true">
              !
            </span>
            <span className="wrap-anywhere">{uploadError}</span>
          </div>
        ) : null}
        <p className="field-hint">支持 pdf / office / 图片 / zip，单个 ≤ 10MB。</p>
        {filesQuery.isPending ? (
          <Loading label="正在读取文件列表…" rows={2} />
        ) : filesQuery.isError ? (
          <ErrorState error={filesQuery.error} title="读取文件失败" onRetry={() => void filesQuery.refetch()} />
        ) : (filesQuery.data?.files ?? []).length === 0 ? (
          <p className="muted small">还没有文件。上传申报书或答辩 PPT，全队在此下载。</p>
        ) : (
          (filesQuery.data?.files ?? []).map((file) => {
            const canDelete = file.uploaderId === meId || isOwner;
            const deleting = removeFile.isPending && removeFile.variables === file.id;
            return (
              <div className="row" key={file.id}>
                <strong className="wrap-anywhere">{file.name}</strong>
                <Tag tone="muted">{formatSize(file.size)}</Tag>
                <span className="muted small">
                  {file.uploaderName} · {formatDateTime(file.createdAt)}
                </span>
                <span className="row-tight right">
                  <Button variant="ghost" size="sm" onClick={() => handleDownload(file.id, file.name)}>
                    下载
                  </Button>
                  {canDelete ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={deleting}
                      onClick={() => removeFile.mutate(file.id)}
                    >
                      删除
                    </Button>
                  ) : null}
                </span>
              </div>
            );
          })
        )}
      </section>

      <hr className="divider" />

      <section className="stack-sm" aria-label="决策日志">
        <span className="card-sub">决策日志（只增不改，可追溯当时定了什么）</span>
        {decisionsQuery.isPending ? (
          <Loading label="正在读取决策日志…" rows={2} />
        ) : decisionsQuery.isError ? (
          <ErrorState error={decisionsQuery.error} title="读取决策日志失败" onRetry={() => void decisionsQuery.refetch()} />
        ) : (decisionsQuery.data?.decisions ?? []).length === 0 ? (
          <p className="muted small">还没有决策记录。定下选题、分工、例会后记一条，全队可查。</p>
        ) : (
          (decisionsQuery.data?.decisions ?? []).map((decision) => (
            <div className="stack-xs" key={decision.id}>
              <div className="row-tight">
                <strong>{decision.title}</strong>
                <span className="muted small">
                  {decision.authorName} · {formatDateTime(decision.createdAt)}
                </span>
              </div>
              {decision.detail.length > 0 ? <p className="muted small wrap-anywhere">{decision.detail}</p> : null}
            </div>
          ))
        )}
        <form className="stack-sm" onSubmit={handleAddDecision} noValidate>
          {decisionError !== null ? (
            <div className="banner banner-danger" role="alert">
              <span className="banner-ico" aria-hidden="true">
                !
              </span>
              <span className="wrap-anywhere">{decisionError}</span>
            </div>
          ) : null}
          <div className="grid-2">
            <div className="field">
              <label className="field-label" htmlFor={`decision-title-${team.id}`}>
                决策标题（必填，60 字内）
              </label>
              <input
                id={`decision-title-${team.id}`}
                className="input"
                value={decisionTitle}
                maxLength={60}
                placeholder="如：确定选题 / 路演由张三主讲"
                onChange={(event) => setDecisionTitle(event.target.value)}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={`decision-detail-${team.id}`}>
                详情（选填，500 字内）
              </label>
              <input
                id={`decision-detail-${team.id}`}
                className="input"
                value={decisionDetail}
                maxLength={500}
                placeholder="补充背景或结论依据"
                onChange={(event) => setDecisionDetail(event.target.value)}
              />
            </div>
          </div>
          <div className="row">
            <Button type="submit" variant="secondary" size="sm" loading={addDecision.isPending}>
              {addDecision.isPending ? '正在记录…' : '记录决策'}
            </Button>
          </div>
        </form>
      </section>

      <hr className="divider" />

      <section className="stack-sm" aria-label="导师反馈">
        <div className="row-tight">
          <span className="card-sub">导师反馈（一次性链接；原文不改写，AI 只追加建议）</span>
          {isOwner && team.advisorId !== null && team.advisorId !== undefined ? (
            <span className="right">
              <Button
                variant="secondary"
                size="sm"
                loading={createFeedbackLink.isPending}
                onClick={() => createFeedbackLink.mutate()}
              >
                生成导师反馈链接
              </Button>
            </span>
          ) : null}
        </div>
        {isOwner && (team.advisorId === null || team.advisorId === undefined) ? (
          <p className="field-hint">
            先在
            <a href="/advisors"> 指导老师页 </a>
            绑定导师，即可生成免登录反馈链接。
          </p>
        ) : null}
        {linkError !== null ? (
          <div className="banner banner-danger" role="alert">
            <span className="banner-ico" aria-hidden="true">
              !
            </span>
            <span className="wrap-anywhere">{linkError}</span>
          </div>
        ) : null}
        {freshLink !== null ? (
          <div className="banner banner-ok" role="status">
            <span className="wrap-anywhere">
              新链接（只显示这一次，7 天内有效、提交后失效）：
              <code className="mono">{`${window.location.origin}${freshLink.url}`}</code>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void navigator.clipboard
                  .writeText(`${window.location.origin}${freshLink.url}`)
                  .then(() => toast.success('已复制', '把链接发给导师，微信/邮件均可'))
                  .catch(() => toast.error('复制失败', '请手动选中链接复制'));
              }}
            >
              复制链接
            </Button>
          </div>
        ) : null}
        {feedbackQuery.isPending ? (
          <Loading label="正在读取导师反馈…" rows={2} />
        ) : feedbackQuery.isError ? (
          <ErrorState error={feedbackQuery.error} title="读取导师反馈失败" onRetry={() => void feedbackQuery.refetch()} />
        ) : (feedbackQuery.data?.feedback ?? []).length === 0 ? (
          <p className="muted small">还没有收到导师反馈。生成链接发给导师，对方免登录就能提交方向性建议。</p>
        ) : (
          (feedbackQuery.data?.feedback ?? []).map((item) => (
            <div className="stack-xs" key={item.id}>
              <div className="row-tight">
                <strong>{item.advisorName}</strong>
                <span className="muted small">{formatDateTime(item.createdAt)}</span>
              </div>
              <p className="wrap-anywhere">{item.content}</p>
              {item.suggestion !== null && item.suggestion !== undefined ? (
                <details className="feedback-suggestion">
                  <summary>
                    <Tag tone={item.suggestionVia === 'llm' ? 'primary' : 'muted'}>
                      AI 行动建议 · via {item.suggestionVia === 'llm' ? 'LLM' : '规则'}
                    </Tag>
                    <span className="muted small">在导师原文之上追加，点击展开/收起</span>
                  </summary>
                  <p className="wrap-anywhere">{item.suggestion}</p>
                </details>
              ) : (
                <p className="muted small">AI 行动建议生成中…（提交后几秒内出现，失败会自动隐藏）</p>
              )}
            </div>
          ))
        )}
      </section>
    </div>
  );
}
